<div class="h-full flex flex-col bg-slate-50">
  <!-- Header -->
  <div class="bg-white border-b border-slate-200 px-6 py-4">
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold text-slate-900">Module Management</h1>
        <p class="text-sm text-slate-600 mt-1">Install and manage application modules</p>
      </div>
      <div class="flex items-center gap-3">
        <input
          type="text"
          [(ngModel)]="searchQuery"
          placeholder="Search modules..."
          class="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <button
          (click)="openConvertModal()"
          class="px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-500 text-white rounded-lg hover:from-purple-700 hover:to-purple-600 transition-all shadow-lg hover:shadow-xl flex items-center gap-2"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
          Convert Client to Module
        </button>
      </div>
    </div>
  </div>

  <!-- Main Content -->
  <div class="flex-1 overflow-auto p-6">
    <!-- Installed Modules Section -->
    <div class="mb-8" *ngIf="modules.length > 0">
      <h2 class="text-lg font-semibold text-slate-900 mb-4">Installed Modules</h2>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div *ngFor="let module of modules" 
             class="bg-white rounded-lg border border-slate-200 p-5 hover:shadow-md transition-shadow">
          <div class="flex items-start justify-between mb-3">
            <div class="flex items-center gap-3">
              <div *ngIf="module.imageUrl" class="w-12 h-12 rounded-lg overflow-hidden">
                <img [src]="module.imageUrl" [alt]="module.name" class="w-full h-full object-cover" />
              </div>
              <div *ngIf="!module.imageUrl" class="w-12 h-12 rounded-lg flex items-center justify-center text-white"
                   [ngClass]="getCategoryColor(module.category)">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <div>
                <h3 class="font-semibold text-slate-900">{{ module.name }}</h3>
                <span class="text-xs text-slate-500">v{{ module.version }}</span>
              </div>
            </div>
            <span class="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">Installed</span>
          </div>
          <p class="text-sm text-slate-600 mb-4">{{ module.description }}</p>
          <div class="flex items-center gap-4 text-xs text-slate-500">
            <span>{{ module.entityCount }} Entities</span>
            <span *ngIf="module.ootbEntityCount > 0">{{ module.ootbEntityCount }} OOTB</span>
            <span>{{ module.pageCount }} Pages</span>
            <span>{{ module.roleCount }} Roles</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Available Modules Section -->
    <div>
      <h2 class="text-lg font-semibold text-slate-900 mb-4">Available Modules</h2>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div *ngFor="let module of filteredAvailableModules" 
             class="bg-white rounded-lg border border-slate-200 p-5 hover:shadow-md transition-shadow cursor-pointer"
             [class.opacity-50]="isModuleInstalled(module.id)"
             (click)="!isModuleInstalled(module.id) && selectModule(module)">
          <div class="flex items-start justify-between mb-3">
            <div class="flex items-center gap-3">
              <div *ngIf="module.imageUrl" class="w-12 h-12 rounded-lg overflow-hidden">
                <img [src]="module.imageUrl" [alt]="module.name" class="w-full h-full object-cover" />
              </div>
              <div *ngIf="!module.imageUrl" class="w-12 h-12 rounded-lg flex items-center justify-center text-white"
                   [ngClass]="getCategoryColor(module.category)">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <div>
                <h3 class="font-semibold text-slate-900">{{ module.name }}</h3>
                <span class="text-xs" [ngClass]="getCategoryColor(module.category).replace('bg-', 'text-')">
                  {{ module.category }}
                </span>
              </div>
            </div>
            <span *ngIf="isModuleInstalled(module.id)" 
                  class="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">Installed</span>
          </div>
          <p class="text-sm text-slate-600 mb-4 line-clamp-2">{{ module.description }}</p>
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-4 text-xs text-slate-500">
              <span>{{ module.entityCount }} Entities</span>
              <span *ngIf="module.ootbEntityCount > 0">{{ module.ootbEntityCount }} OOTB</span>
              <span>{{ module.pageCount }} Pages</span>
              <span>{{ module.roleCount }} Roles</span>
            </div>
            <button 
              *ngIf="!isModuleInstalled(module.id)"
              (click)="installModule(module); $event.stopPropagation()"
              [disabled]="isInstalling"
              class="px-3 py-1.5 bg-indigo-600 text-white text-xs rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {{ isInstalling ? 'Installing...' : 'Install' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div *ngIf="filteredAvailableModules.length === 0" 
           class="text-center py-12">
        <svg xmlns="http://www.w3.org/2000/svg" class="w-16 h-16 mx-auto text-slate-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <p class="text-slate-500">No modules found matching "{{ searchQuery }}"</p>
      </div>
    </div>
  </div>

  <!-- Module Detail Modal (Overview) -->
  <div *ngIf="selectedModule && !showSetup" 
       class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
       (click)="selectedModule = null">
    <div class="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-auto" 
         (click)="$event.stopPropagation()">
      <!-- Header -->
      <div class="flex items-start justify-between mb-6">
        <div class="flex items-center gap-4">
          <div *ngIf="selectedModule.imageUrl" class="w-20 h-20 rounded-lg overflow-hidden shadow-lg">
            <img [src]="selectedModule.imageUrl" [alt]="selectedModule.name" class="w-full h-full object-cover" />
          </div>
          <div *ngIf="!selectedModule.imageUrl" class="w-20 h-20 rounded-lg flex items-center justify-center text-white shadow-lg"
               [ngClass]="getCategoryColor(selectedModule.category)">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <div>
            <h2 class="text-2xl font-bold text-slate-900">{{ selectedModule.name }}</h2>
            <p class="text-sm text-slate-600 mt-1">{{ selectedModule.description }}</p>
            <span class="text-xs text-slate-400 mt-1 block">Version {{ selectedModule.version }} • Client: {{ selectedModule.clientId }}</span>
          </div>
        </div>
        <button (click)="selectedModule = null" 
                class="text-slate-400 hover:text-slate-600">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Summary Grid -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div class="bg-indigo-50 rounded-lg p-4 text-center">
          <div class="text-3xl font-bold text-indigo-900">{{ selectedModule.ootbEntityCount }}</div>
          <div class="text-xs text-indigo-600 mt-1">OOTB Entities</div>
        </div>
        <div class="bg-slate-50 rounded-lg p-4 text-center">
          <div class="text-3xl font-bold text-slate-900">{{ selectedModule.entityCount }}</div>
          <div class="text-xs text-slate-600 mt-1">Entities</div>
        </div>
        <div class="bg-emerald-50 rounded-lg p-4 text-center">
          <div class="text-3xl font-bold text-emerald-900">{{ selectedModule.pageCount }}</div>
          <div class="text-xs text-emerald-600 mt-1">Pages</div>
        </div>
        <div class="bg-amber-50 rounded-lg p-4 text-center">
          <div class="text-3xl font-bold text-amber-900">{{ selectedModule.roleCount }}</div>
          <div class="text-xs text-amber-600 mt-1">Roles</div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex gap-3">
        <button 
          (click)="openSetup()"
          class="flex-1 px-6 py-3 bg-gradient-to-r from-indigo-600 to-indigo-500 text-white rounded-lg hover:from-indigo-700 hover:to-indigo-600 transition-all font-medium shadow-lg hover:shadow-xl">
          Setup Import
        </button>
        <button 
          (click)="selectedModule = null"
          class="px-6 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors">
          Cancel
        </button>
      </div>
    </div>
  </div>

  <!-- Module Setup Modal (Detailed Selection) -->
  <div *ngIf="selectedModule && showSetup" 
       class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
       (click)="closeSetup()">
    <div class="bg-white rounded-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-auto" 
         (click)="$event.stopPropagation()">
      <!-- Header -->
      <div class="flex items-start justify-between mb-6">
        <div class="flex items-center gap-4">
          <div *ngIf="selectedModule.imageUrl" class="w-16 h-16 rounded-lg overflow-hidden shadow-md">
            <img [src]="selectedModule.imageUrl" [alt]="selectedModule.name" class="w-full h-full object-cover" />
          </div>
          <div *ngIf="!selectedModule.imageUrl" class="w-16 h-16 rounded-lg flex items-center justify-center text-white shadow-md"
               [ngClass]="getCategoryColor(selectedModule.category)">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <div>
            <h2 class="text-2xl font-bold text-slate-900">Setup {{ selectedModule.name }}</h2>
            <p class="text-sm text-slate-600 mt-1">Select components to import to your workspace</p>
          </div>
        </div>
        <button (click)="closeSetup()" 
                class="text-slate-400 hover:text-slate-600">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Loading State -->
      <div *ngIf="isLoadingModuleDetails" class="text-center py-12">
        <div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        <p class="text-slate-600 mt-4">Loading module contents...</p>
      </div>

      <!-- Module Contents -->
      <div *ngIf="!isLoadingModuleDetails">
        <!-- Info Note -->
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div class="flex items-start gap-3">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-blue-600 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div class="text-sm text-blue-800">
              <p class="font-medium mb-1">Import Instructions (Step-by-Step)</p>
              <p>1. Select all OOTB Entities (required first step)</p>
              <p>2. Select all Entities (unlocks after OOTB complete)</p>
              <p>3. Select Pages (unlocks after all Entities selected)</p>
              <p>4. Roles will be imported automatically</p>
            </div>
          </div>
        </div>

        <!-- Grid Layout for Components -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <!-- OOTB Entities (Selectable - First Step) -->
          <div *ngIf="moduleOotbEntities.length > 0" 
               class="bg-indigo-50 border-2 border-indigo-200 rounded-lg p-4">
            <div class="flex items-center gap-2 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 class="font-bold text-indigo-900 text-lg">OOTB Entities</h3>
              <span class="text-sm text-indigo-700">({{ selectedOotbEntityIds.size }}/{{ moduleOotbEntities.length }} selected)</span>
              <button 
                (click)="selectAllOotbEntities()"
                class="ml-auto text-sm text-indigo-600 hover:text-indigo-700 font-medium">
                Select All
              </button>
            </div>
            <div class="space-y-2 max-h-60 overflow-y-auto border border-indigo-100 rounded-lg p-3 bg-white">
              <div *ngFor="let entity of moduleOotbEntities" 
                   (click)="toggleOotbEntitySelection(entity.id)"
                   class="bg-white border rounded-lg p-3 flex items-center gap-3 cursor-pointer transition-all hover:shadow-md"
                   [class.border-indigo-300]="!selectedOotbEntityIds.has(entity.id)"
                   [class.bg-indigo-50]="selectedOotbEntityIds.has(entity.id)"
                   [class.border-indigo-500]="selectedOotbEntityIds.has(entity.id)"
                   [class.shadow-sm]="selectedOotbEntityIds.has(entity.id)">
                <input 
                  type="checkbox" 
                  [checked]="selectedOotbEntityIds.has(entity.id)" 
                  class="rounded text-indigo-600"
                  (click)="$event.stopPropagation()" />
                <svg xmlns="http://www.w3.org/2000/svg" 
                     class="w-5 h-5"
                     [class.text-indigo-400]="!selectedOotbEntityIds.has(entity.id)"
                     [class.text-indigo-600]="selectedOotbEntityIds.has(entity.id)"
                     fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                </svg>
                <span class="text-sm font-medium flex-1"
                      [class.text-slate-900]="!selectedOotbEntityIds.has(entity.id)"
                      [class.text-indigo-900]="selectedOotbEntityIds.has(entity.id)">
                  {{ entity.name }}
                </span>
              </div>
            </div>
          </div>

          <!-- Roles (Read-only) -->
          <div *ngIf="moduleRoles.length > 0" 
               class="bg-amber-50 border-2 border-amber-200 rounded-lg p-4">
            <div class="flex items-center gap-2 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
              <h3 class="font-bold text-amber-900 text-lg">Roles</h3>
              <span class="ml-auto bg-amber-200 text-amber-900 px-2 py-1 rounded-full text-xs font-medium">Auto-Import</span>
            </div>
            <div class="space-y-2 max-h-60 overflow-y-auto">
              <div *ngFor="let role of moduleRoles" 
                   class="bg-white rounded-lg p-3 flex items-center gap-3 border border-amber-100">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                <span class="text-sm font-medium text-amber-900">{{ role.name }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Entities (Selectable - Enabled after OOTB complete) -->
        <div class="mb-6" *ngIf="moduleEntities.length > 0">
          <div class="flex items-center justify-between mb-3">
            <h3 class="font-bold text-slate-900 text-lg flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
              </svg>
              Entities
              <span class="text-sm font-normal text-slate-600">({{ selectedEntityIds.size }}/{{ moduleEntities.length }} selected)</span>
            </h3>
            <div class="flex items-center gap-3">
              <span *ngIf="!canSelectEntities" class="text-xs text-amber-700 bg-amber-100 px-3 py-1.5 rounded-full font-medium">
                ⚠️ Select all OOTB entities and entities first
              </span>
              <button 
                *ngIf="canSelectEntities"
                (click)="selectAllEntities()"
                class="text-sm text-indigo-600 hover:text-indigo-700 font-medium">
                Select All
              </button>
            </div>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-60 overflow-y-auto border border-slate-200 rounded-lg p-3"
               [class.bg-slate-50]="canSelectEntities"
               [class.bg-slate-100]="!canSelectEntities"
               [class.opacity-60]="!canSelectEntities"
               [class.pointer-events-none]="!canSelectEntities">
            <div *ngFor="let entity of moduleEntities" 
                 (click)="canSelectEntities && toggleEntitySelection(entity.id)"
                 class="border rounded-lg p-3 flex items-center gap-3 transition-all"
                 [class.cursor-pointer]="canSelectEntities"
                 [class.hover:shadow-md]="canSelectEntities"
                 [class.bg-white]="!selectedEntityIds.has(entity.id)"
                 [class.border-slate-300]="!selectedEntityIds.has(entity.id)"
                 [class.bg-emerald-50]="selectedEntityIds.has(entity.id) && canSelectEntities"
                 [class.border-emerald-500]="selectedEntityIds.has(entity.id) && canSelectEntities"
                 [class.shadow-sm]="selectedEntityIds.has(entity.id) && canSelectEntities">
              <input 
                type="checkbox" 
                [checked]="selectedEntityIds.has(entity.id)" 
                [disabled]="!canSelectEntities"
                class="rounded text-emerald-600"
                (click)="$event.stopPropagation()" />
              <svg xmlns="http://www.w3.org/2000/svg" 
                   class="w-5 h-5" 
                   [class.text-slate-400]="!canSelectEntities"
                   [class.text-slate-600]="canSelectEntities && !selectedEntityIds.has(entity.id)"
                   [class.text-emerald-600]="selectedEntityIds.has(entity.id) && canSelectEntities"
                   fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
              </svg>
              <span class="text-sm font-medium flex-1"
                    [class.text-slate-400]="!canSelectEntities"
                    [class.text-slate-900]="canSelectEntities && !selectedEntityIds.has(entity.id)"
                    [class.text-emerald-900]="selectedEntityIds.has(entity.id) && canSelectEntities">
                {{ entity.name }}
              </span>
            </div>
          </div>
        </div>

        <!-- Pages (Selectable, enabled only when all entities selected) -->
        <div class="mb-6" *ngIf="modulePages.length > 0">
          <div class="flex items-center justify-between mb-3">
            <h3 class="font-bold text-slate-900 text-lg flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Pages
              <span class="text-sm font-normal text-slate-600">({{ selectedPageIds.size }}/{{ modulePages.length }} selected)</span>
            </h3>
            <div class="flex items-center gap-3">
              <span *ngIf="!canImportPages" class="text-xs text-amber-700 bg-amber-100 px-3 py-1.5 rounded-full font-medium">
                ⚠️ Select all OOTB entities and entities first
              </span>
              <button 
                *ngIf="canImportPages"
                (click)="selectAllPages()"
                class="text-sm text-indigo-600 hover:text-indigo-700 font-medium">
                Select All
              </button>
            </div>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-60 overflow-y-auto border border-slate-200 rounded-lg p-3"
               [class.bg-slate-50]="canImportPages"
               [class.bg-slate-100]="!canImportPages"
               [class.opacity-60]="!canImportPages"
               [class.pointer-events-none]="!canImportPages">
            <div *ngFor="let page of modulePages" 
                 (click)="canImportPages && togglePageSelection(page.id)"
                 class="border rounded-lg p-3 flex items-center gap-3 transition-all"
                 [class.cursor-pointer]="canImportPages"
                 [class.hover:shadow-md]="canImportPages"
                 [class.bg-white]="!selectedPageIds.has(page.id)"
                 [class.border-slate-300]="!selectedPageIds.has(page.id)"
                 [class.bg-emerald-50]="selectedPageIds.has(page.id) && canImportPages"
                 [class.border-emerald-500]="selectedPageIds.has(page.id) && canImportPages"
                 [class.shadow-sm]="selectedPageIds.has(page.id) && canImportPages">
              <input 
                type="checkbox" 
                [checked]="selectedPageIds.has(page.id)" 
                [disabled]="!canImportPages"
                class="rounded text-emerald-600"
                (click)="$event.stopPropagation()" />
              <svg xmlns="http://www.w3.org/2000/svg" 
                   class="w-5 h-5" 
                   [class.text-slate-400]="!canImportPages"
                   [class.text-slate-600]="canImportPages && !selectedPageIds.has(page.id)"
                   [class.text-emerald-600]="selectedPageIds.has(page.id) && canImportPages"
                   fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span class="text-sm font-medium flex-1"
                    [class.text-slate-400]="!canImportPages"
                    [class.text-slate-900]="canImportPages && !selectedPageIds.has(page.id)"
                    [class.text-emerald-900]="selectedPageIds.has(page.id) && canImportPages">
                {{ page.name }}
              </span>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex gap-3 pt-6 border-t-2 border-slate-200">
          <button 
            (click)="installModuleWithSelections()"
            [disabled]="isInstalling || isLoadingModuleDetails || selectedOotbEntityIds.size === 0"
            class="flex-1 px-6 py-3 bg-gradient-to-r from-indigo-600 to-indigo-500 text-white rounded-lg hover:from-indigo-700 hover:to-indigo-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-lg hover:shadow-xl">
            <span *ngIf="!isInstalling">Import Selected Components</span>
            <span *ngIf="isInstalling" class="flex items-center justify-center gap-2">
              <svg class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Importing...
            </span>
          </button>
          <button 
            (click)="closeSetup()"
            [disabled]="isInstalling"
            class="px-6 py-3 border-2 border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium">
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Convert Client to Module Modal -->
  <div *ngIf="showConvertModal" 
       class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
       (click)="closeConvertModal()">
    <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4" 
         (click)="$event.stopPropagation()">
      <div class="flex items-start justify-between mb-4">
        <div class="flex items-center gap-3">
          <div class="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-600 to-purple-400 flex items-center justify-center text-white shadow-lg">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
            </svg>
          </div>
          <div>
            <h2 class="text-xl font-bold text-slate-900">Convert to Module</h2>
            <p class="text-sm text-slate-500">Create a reusable module</p>
          </div>
        </div>
        <button (click)="closeConvertModal()" 
                class="text-slate-400 hover:text-slate-600">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div class="mb-4">
        <label class="block text-sm font-medium text-slate-700 mb-2">Module Name *</label>
        <input
          type="text"
          [(ngModel)]="convertModuleName"
          (input)="validateName()"
          placeholder="e.g., My Custom Module"
          class="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
          [class.border-red-500]="nameValidationError"
          [class.border-slate-300]="!nameValidationError"
        />
        <p *ngIf="nameValidationError" class="text-red-600 text-xs mt-1">{{ nameValidationError }}</p>
        <p class="text-xs text-slate-500 mt-1">Only letters, numbers, spaces, hyphens, and underscores allowed. No "system" word.</p>
      </div>

      <div class="mb-4">
        <label class="block text-sm font-medium text-slate-700 mb-2">Module Image (optional)</label>
        <div class="flex items-center gap-3">
          <input
            type="file"
            #fileInput
            accept="image/*"
            (change)="onImageSelected($event)"
            class="hidden"
          />
          <button
            type="button"
            (click)="fileInput.click()"
            class="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors text-sm">
            Choose Image
          </button>
          <div *ngIf="convertModuleImageUrl" class="w-16 h-16 rounded-lg overflow-hidden border border-slate-200">
            <img [src]="convertModuleImageUrl" alt="Preview" class="w-full h-full object-cover" />
          </div>
          <span *ngIf="selectedImageFile" class="text-sm text-slate-600">{{ selectedImageFile.name }}</span>
        </div>
        <p class="text-xs text-slate-500 mt-1">Max size: 5MB. Formats: JPG, PNG, GIF</p>
      </div>

      <div class="mb-6">
        <label class="block text-sm font-medium text-slate-700 mb-2">Description (optional)</label>
        <textarea
          [(ngModel)]="convertModuleDescription"
          placeholder="Brief description of this module..."
          rows="3"
          class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
        ></textarea>
      </div>

      <div class="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-6">
        <p class="text-sm text-purple-800">
          <strong>Note:</strong> This will capture all current entities (separated as normal and OOTB), pages, roles, and functions from your client and save them as a reusable module.
        </p>
      </div>

      <!-- Functions Check Section -->
      <div class="mb-6">
        <!-- Loading State -->
        <div *ngIf="isCheckingFunctions" class="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
          <p class="text-sm text-blue-800 flex items-center gap-2">
            <svg class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Checking Functions...
          </p>
        </div>
        
        <!-- Functions with Entity Reference -->
        <div *ngIf="functionsChecked && functionsToMove.length > 0" class="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <p class="text-sm font-medium text-blue-900 mb-2">Functions to move to module ({{ functionsToMove.length }}):</p>
          <ul class="text-sm text-blue-800 space-y-1">
            <li *ngFor="let func of functionsToMove" class="flex items-center gap-2">
              <svg class="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z"></path>
              </svg>
              {{ func.name }}
            </li>
          </ul>
        </div>
        
        <!-- Functions without Entity Reference (Archive Selection) -->
        <div *ngIf="functionsChecked && functionsToMoveToArchive.length > 0" class="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
          <div class="flex items-center justify-between mb-3">
            <h4 class="text-sm font-medium text-orange-900">Functions to archive ({{ selectedFunctionsToArchiveIds.size }}/{{ functionsToMoveToArchive.length }} selected)</h4>
            <button 
              (click)="selectAllFunctionsToArchive()"
              class="text-xs text-orange-600 hover:text-orange-700 font-medium">
              Select All
            </button>
          </div>
          <div class="space-y-2 max-h-40 overflow-y-auto">
            <div *ngFor="let func of functionsToMoveToArchive" 
                 (click)="toggleFunctionArchiveSelection(func.id)"
                 class="bg-white border rounded-lg p-3 flex items-center gap-3 cursor-pointer transition-all hover:shadow-sm"
                 [class.border-orange-300]="!selectedFunctionsToArchiveIds.has(func.id)"
                 [class.bg-orange-50]="selectedFunctionsToArchiveIds.has(func.id)"
                 [class.border-orange-500]="selectedFunctionsToArchiveIds.has(func.id)">
              <input 
                type="checkbox" 
                [checked]="selectedFunctionsToArchiveIds.has(func.id)" 
                class="rounded text-orange-600"
                (click)="$event.stopPropagation()" />
              <svg xmlns="http://www.w3.org/2000/svg" 
                   class="w-5 h-5"
                   [class.text-orange-400]="!selectedFunctionsToArchiveIds.has(func.id)"
                   [class.text-orange-600]="selectedFunctionsToArchiveIds.has(func.id)"
                   fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4v.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span class="text-sm font-medium flex-1"
                    [class.text-slate-900]="!selectedFunctionsToArchiveIds.has(func.id)"
                    [class.text-orange-900]="selectedFunctionsToArchiveIds.has(func.id)">
                {{ func.name }}
              </span>
            </div>
          </div>
        </div>
        
        <div *ngIf="functionsChecked && functionsToMove.length === 0 && functionsToMoveToArchive.length === 0" class="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <p class="text-sm text-yellow-800">No functions found to process.</p>
        </div>
      </div>

      <div class="flex gap-3">
        <button 
          (click)="convertClientToModule()"
          [disabled]="isConverting || !convertModuleName.trim() || nameValidationError || !functionsChecked"
          class="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-500 text-white rounded-lg hover:from-purple-700 hover:to-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
          {{ isConverting ? 'Converting...' : 'Convert to Module' }}
        </button>
        <button 
          (click)="closeConvertModal()"
          [disabled]="isConverting"
          class="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50">
          Cancel
        </button>
      </div>
    </div>
  </div>
</div>

